import { flow } from 'lodash/fp';
import { cloneDeep } from 'lodash';

import encodeSpec from 'utils/chart/encode-spec';

import { deepFreeze } from 'utils/deep-freeze';

import { UNIT_ARRAY, UNIT_OBJECT } from 'constants/unit';

import binDocumentsTemporal from './temporal-binning';
import binDocumentsQuantitative from './quantitative-binning';
import chartValueSimplification from './chart-value-simplification';
import handleNoDocuments from './handle-no-documents';

import encodeColumnSortOrder from './table/encode-column-sort-order';
import tableDynamicColumns from './table/table-dynamic-columns';
import tableTotals from './table/table-totals';
import sortTableDocuments from './table/sort-table-documents';
import tableElementFormatting from './table/table-element-formatting';
import setTableCellRenderer from './table/set-table-cell-renderer';
import createColumnGroups from './table/create-column-groups';
import sortTableColumns from './table/sort-table-columns';
import resizeTableColumns from './table/resize-table-columns';
import generateColumnIds from './table/generate-column-ids';
import generateGeopointProperties from './map/generate-geopoint-properties';
import generateMapMetadata from './map/generate-map-metadata';
import generateMapLegends from './map/generate-map-legends';
import generateChoroplethData from './map/generate-choropleth-data';

import { cacheStageData } from './cache-stage-data';

/**
 * This is where we run the generate the final specs & documents for our charts.
 *
 * *** Why do this? ***
 * This is because while we try to model as much as we can in the aggregation pipeline,
 * there are some type of processing which isn't suitable / easy to translate to an aggregation.
 *
 * In particular, all our processing can be broken down into combinations of specification related,
 * or data related changes.
 *
 * - The Specification changes the Data (Temporal binning needs date formatting that can only really be done client side)
 * - The Data changes the Specification (Dynamic Columns for Tables change the spec based off data we get)
 *
 * *** Stage Data ***
 * All the stages are given a stage data object. This consists of 5 parts:
 * - Spec: An object of the chart specification at that point. A stage can return this to signify that it's changed that value.
 * - Documents: An array of objects, each of which are data points / table rows etc. Sourced from the aggregation pipeline initially.
 * - Metadata: Fields that are either drived from data, or augment data. Geospatial colours fit under this, as we shouldn't change every document.
 * - Cache: An object which is meant to be whatever is in the cache reducer. Used for shared, large data for generating charts, like GeoJSON topology.
 * - Environment: An object which contains any chart attributes that should _never_ be changed. Essentially definitional chart properties.
 *
 * *** What is in a stage? ***
 * Each stage is an object that consists of two parts:
 * - A `fn`, which is the function body of the stage, and accepts a stage data object. It returns only what it changed.
 * - An `input` array, which contains the paths to deep pick from the stage data.
 *
 * The `input` array is important to limit how often the stage needs to rerun. If the picked data doesn't change, we will not rerun that stage.
 *
 * *** Developing stages ***
 * In developer mode, we run a bunch of compliance checks to make sure we don't make silly mistakes around what the stages can accept / return.
 *
 * The checks aren't foolproof, but they should be sufficient to prevent accidentally breaking other stages.
 *
 * You're recommended to make use of `complies` from `compliance-utils` in your tests, as it'll make it easier to determine if your stage is making a mistake.
 */

/**
 * A builder function for the processSpecAndData pipeline.
 *
 * All stages are wrapped by cacheStageData to allow calling them multiple times without
 * rerunning them, unless their inputs have changed.
 * @returns {Function} A function which applys the given arguments to the cached stages
 */
export function lazyProcessSpecAndData() {
  const functions = [
    binDocumentsTemporal,
    binDocumentsQuantitative,
    encodeColumnSortOrder,
    chartValueSimplification,
    setTableCellRenderer,
    tableElementFormatting,
    tableDynamicColumns,
    sortTableDocuments,
    tableTotals,
    sortTableColumns,
    createColumnGroups,
    resizeTableColumns,
    generateColumnIds,
    generateMapMetadata,
    generateGeopointProperties,
    generateChoroplethData,
    generateMapLegends,
    handleNoDocuments,
  ].map(cacheStageData);

  return (
    chartType,
    channels,
    reductions,
    customisations,
    meta,
    data,
    cache
  ) => {
    // deep clone the individual properties as the reducer can mutate the state
    // and deep freeze to prevent future stages from mutating the environment
    const environment = deepFreeze(
      cloneDeep({ chartType, channels, reductions, customisations, meta })
    );

    const seedSpec = encodeSpec(
      chartType,
      channels,
      reductions,
      customisations,
      meta,
      data.metadata || UNIT_OBJECT
    );

    const initialStageData = {
      documents: data.documents || UNIT_ARRAY,
      metadata: data.metadata || UNIT_OBJECT,
      spec: seedSpec,
      environment,
      cache,
    };

    const stageData = flow(functions)(initialStageData);

    return {
      data: { documents: stageData.documents, metadata: stageData.metadata },
      spec: stageData.spec,
    };
  };
}
